import axios from '@/plugins/http/axios.js';
import config from "@/views/surveys/config/surveys_api.js";
import PathUri from '@/utils/path_uri';

const _pathUri = new PathUri(config.port, config.context);

function buildUrl(idGroup, path) {
    return `${config.baseUrl + _pathUri.generateUri() + idGroup}${path}`;
}

function headers(idEmpresa, idUsuario) {
    return {
        'idEmpresa': idEmpresa,
        'idUsuario': idUsuario
    };
}

export const service = {
    /* ---------- Configuración y tipos ---------- */
    getConfig: (idGroup, idEmpresa, idUsuario) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, '/surveys/core/config'),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    putConfig: (idGroup, idEmpresa, idUsuario, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, '/surveys/core/config'),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    getQuestionTypes: (idGroup, idEmpresa, idUsuario) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, '/surveys/core/question-types'),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    /* ---------- Encuestas ---------- */
    getListSurveys: (idGroup, idEmpresa, idUsuario, params = {}) => {
        const query = new URLSearchParams();
        if (params.page !== undefined) query.append('page', params.page);
        if (params.size !== undefined) query.append('size', params.size);
        if (params.status) query.append('status', params.status);
        if (params.title) query.append('title', params.title);

        const queryString = query.toString();
        const url = buildUrl(idGroup, `/surveys/core${queryString ? '?' + queryString : ''}`);

        return axios({
            method: 'get',
            url: url,
            headers: headers(idEmpresa, idUsuario)
        })
    },

    getSurveyById: (idGroup, idEmpresa, idUsuario, surveyId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}`),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    postCreateSurvey: (idGroup, idEmpresa, idUsuario, data) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, '/surveys/core'),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    putUpdateSurvey: (idGroup, idEmpresa, idUsuario, surveyId, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    patchSurveyStatus: (idGroup, idEmpresa, idUsuario, surveyId, status) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}/status`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: { status }
        })
    },

    deleteSurvey: (idGroup, idEmpresa, idUsuario, surveyId) => {
        return axios({
            method: 'delete',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}`),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    /* ---------- Resultados ---------- */
    getResponses: (idGroup, idEmpresa, idUsuario, surveyId, params = {}) => {
        const query = new URLSearchParams();
        if (params.page !== undefined) query.append('page', params.page);
        if (params.size !== undefined) query.append('size', params.size);
        if (params.from) query.append('from', params.from);
        if (params.to) query.append('to', params.to);

        const queryString = query.toString();
        const url = buildUrl(idGroup, `/surveys/core/${surveyId}/responses${queryString ? '?' + queryString : ''}`);

        return axios({
            method: 'get',
            url: url,
            headers: headers(idEmpresa, idUsuario)
        })
    },

    getSummary: (idGroup, idEmpresa, idUsuario, surveyId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}/summary`),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    getOverview: (idGroup, idEmpresa, idUsuario, surveyId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}/overview`),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    /* ---------- Bot de estrellas ---------- */
    putBotConfig: (idGroup, idEmpresa, idUsuario, surveyId, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}/bot-config`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    deleteBotConfig: (idGroup, idEmpresa, idUsuario, surveyId) => {
        return axios({
            method: 'delete',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}/bot-config`),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    /* ---------- Preguntas ---------- */
    postCreateQuestion: (idGroup, idEmpresa, idUsuario, surveyId, data) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}/questions`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    putUpdateQuestion: (idGroup, idEmpresa, idUsuario, questionId, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/surveys/core/questions/${questionId}`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    deleteQuestion: (idGroup, idEmpresa, idUsuario, questionId) => {
        return axios({
            method: 'delete',
            url: buildUrl(idGroup, `/surveys/core/questions/${questionId}`),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    putReorderQuestions: (idGroup, idEmpresa, idUsuario, surveyId, questionIds) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/surveys/core/${surveyId}/questions/reorder`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: { questionIds }
        })
    },

    /* ---------- Opciones ---------- */
    postCreateOption: (idGroup, idEmpresa, idUsuario, questionId, data) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/surveys/core/questions/${questionId}/options`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    putUpdateOption: (idGroup, idEmpresa, idUsuario, optionId, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/surveys/core/questions/options/${optionId}`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: data
        })
    },

    deleteOption: (idGroup, idEmpresa, idUsuario, optionId) => {
        return axios({
            method: 'delete',
            url: buildUrl(idGroup, `/surveys/core/questions/options/${optionId}`),
            headers: headers(idEmpresa, idUsuario)
        })
    },

    putReorderOptions: (idGroup, idEmpresa, idUsuario, questionId, optionIds) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/surveys/core/questions/${questionId}/options/reorder`),
            headers: {
                ...headers(idEmpresa, idUsuario),
                'Content-Type': 'application/json'
            },
            data: { optionIds }
        })
    }
}
